import * as React from "react"
import { Link, createFileRoute, notFound } from "@tanstack/react-router"
import {
  CalendarPlus,
  ChevronLeft,
  Download,
  MapPin,
  Share2,
} from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { InquiryForm } from "@/components/inquiry-form"
import {
  FavoriteButton,
  FeatureFlags,
  PropertyCard,
  PropertyFacts,
} from "@/components/property-display"
import { PropertyMap } from "@/components/property-map"
import { getPropertyDetailFn } from "@/lib/app.functions"
import { formatPrice, titleCase } from "@/lib/constants"

export const Route = createFileRoute("/property/$slug")({
  loader: async ({ params }) => {
    const result = await getPropertyDetailFn({ data: { slug: params.slug } })
    if (!result) {
      throw notFound()
    }
    return result
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData
          ? `${loaderData.property.title} · Harbor & Pine Realty`
          : "Property · Harbor & Pine Realty",
      },
      {
        name: "description",
        content: loaderData?.property.description.slice(0, 150),
      },
    ],
  }),
  component: PropertyPage,
})

function PropertyPage() {
  const { viewer, property, suggested } = Route.useLoaderData()
  const [selectedImage, setSelectedImage] = React.useState(
    property.images.at(0)?.url ?? ""
  )
  const mapProperties = React.useMemo(() => [property], [property])

  const handleShare = async () => {
    const share = {
      title: property.title,
      text: `${property.title} · ${property.reference}`,
      url: window.location.href,
    }
    try {
      const nativeShare: unknown = Reflect.get(navigator, "share")
      if (typeof nativeShare === "function") {
        await nativeShare.call(navigator, share)
      } else {
        await navigator.clipboard.writeText(window.location.href)
        toast.success("Property link copied.")
      }
    } catch {
      // Native share cancellation needs no error message.
    }
  }

  return (
    <div className="field-container py-8">
      <Link
        to="/search"
        className="inline-flex items-center gap-2 text-sm font-semibold hover:text-primary"
      >
        <ChevronLeft className="size-4" />
        Back to property search
      </Link>

      <header className="mt-6 grid gap-6 border-b-2 border-foreground pb-7 lg:grid-cols-[1fr_auto]">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <span className="field-label">{property.reference}</span>
            <span className="h-4 w-px bg-border" />
            <span className="field-label">{titleCase(property.type)}</span>
            <span className="h-4 w-px bg-border" />
            <span className="field-label">
              {titleCase(property.transaction)}
            </span>
          </div>
          <h1 className="field-heading mt-3">{property.title}</h1>
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="size-4 text-primary" />
            {property.address}, {property.locality}, {property.region}
          </p>
        </div>
        <div className="flex flex-col items-start gap-4 lg:items-end">
          <p className="font-heading text-4xl leading-none font-bold">
            {formatPrice(
              property.price,
              property.transaction,
              property.rentPeriod
            )}
          </p>
          <div className="flex gap-2">
            <FavoriteButton
              propertyId={property.id}
              initialFavorite={property.favorite}
              signedIn={Boolean(viewer)}
              returnTo={`/property/${property.slug}`}
            />
            <Button
              variant="outline"
              className="field-button"
              onClick={handleShare}
            >
              <Share2 />
              Share
            </Button>
            <Button
              variant="outline"
              className="field-button"
              nativeButton={false}
              render={
                <Link
                  to="/brochure/$slug"
                  params={{ slug: property.slug }}
                  target="_blank"
                />
              }
            >
              <Download />
              Brochure
            </Button>
          </div>
        </div>
      </header>

      <section className="mt-7 grid gap-3 lg:grid-cols-[1.5fr_0.5fr]">
        <Dialog>
          <DialogTrigger
            className="relative min-h-[420px] overflow-hidden bg-muted lg:min-h-[620px]"
            onClick={() => setSelectedImage(property.images[0]?.url ?? "")}
          >
            {property.images[0] ? (
              <img
                src={property.images[0].url}
                alt={property.images[0].alt}
                className="size-full object-cover"
              />
            ) : null}
            <span className="absolute right-4 bottom-4 border border-foreground bg-white px-3 py-2 font-heading text-xs font-bold uppercase">
              Open gallery
            </span>
          </DialogTrigger>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {property.images.slice(1, 3).map((image) => (
              <DialogTrigger
                key={image.id}
                className="min-h-52 overflow-hidden bg-muted"
                onClick={() => setSelectedImage(image.url)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="size-full object-cover"
                />
              </DialogTrigger>
            ))}
          </div>
          <DialogContent className="max-w-6xl rounded-none border-0 bg-foreground p-2">
            <DialogTitle className="sr-only">
              {property.title} gallery
            </DialogTitle>
            <img
              src={selectedImage}
              alt={`${property.title} enlarged view`}
              className="max-h-[85vh] w-full object-contain"
            />
          </DialogContent>
        </Dialog>
      </section>

      <PropertyFacts property={property} />

      <div className="mt-12 grid items-start gap-10 lg:grid-cols-[minmax(0,1.25fr)_380px]">
        <article className="min-w-0">
          <p className="field-label">Property overview</p>
          <h2 className="mt-2 font-heading text-4xl uppercase">
            About this property
          </h2>
          <p className="mt-6 text-base leading-8 whitespace-pre-line text-muted-foreground">
            {property.description}
          </p>
          <div className="mt-7">
            <FeatureFlags property={property} />
          </div>

          <section className="mt-10 border-t-2 border-foreground pt-6">
            <h2 className="font-heading text-3xl uppercase">Amenities</h2>
            <ul className="mt-5 grid gap-px border bg-border sm:grid-cols-2">
              {property.amenities.map((amenity) => (
                <li
                  key={amenity}
                  className="flex min-h-14 items-center gap-3 bg-white px-4 text-sm font-semibold"
                >
                  <span className="size-2 bg-accent ring-1 ring-foreground" />
                  {amenity}
                </li>
              ))}
            </ul>
          </section>

          <section className="mt-10 border-t-2 border-foreground pt-6">
            <h2 className="font-heading text-3xl uppercase">Exact location</h2>
            <PropertyMap
              properties={mapProperties}
              openFirstPopup
              className="mt-5 min-h-[460px]"
            />
            <p className="mt-3 text-xs text-muted-foreground">
              The pin uses the listing’s exact demo coordinates. The property,
              address, and location data are fictional.
            </p>
          </section>
        </article>

        <aside className="field-panel p-5 lg:sticky lg:top-24">
          <div className="flex items-start gap-3 border-b pb-4">
            <span className="grid size-10 place-items-center bg-accent">
              <CalendarPlus className="size-5" />
            </span>
            <div>
              <p className="field-label">Ask the next question</p>
              <p className="mt-1 text-sm text-muted-foreground">
                The demo inquiry appears in member and admin workspaces.
              </p>
            </div>
          </div>
          <div className="pt-5">
            <InquiryForm
              propertyId={property.id}
              propertyTitle={property.title}
              viewer={viewer}
            />
          </div>
        </aside>
      </div>

      {suggested.length > 0 ? (
        <section className="mt-20 border-t-2 border-foreground pt-8">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="field-label">Similar properties</p>
              <h2 className="mt-2 font-heading text-4xl uppercase">
                Keep the comparison useful
              </h2>
            </div>
            <Link to="/search" className="field-link text-sm font-semibold">
              See all listings
            </Link>
          </div>
          <div className="mt-6 grid gap-5 lg:grid-cols-3">
            {suggested.map((item) => (
              <PropertyCard
                key={item.id}
                property={item}
                signedIn={Boolean(viewer)}
                className="md:grid-cols-1"
              />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  )
}
